import { request } from './client'
import { asArray, asObject, bool, count, num, str, text } from './parse'

/**
 * The account's own conversations, and letting one into the CRM.
 *
 * Nothing here is cached across screens on purpose. The dialog list is the
 * owner's private life — their family, their doctor, their landlord — and the
 * server does not store it precisely so that it exists only while somebody is
 * looking at it. Keeping a copy in a query cache that outlives the modal would
 * put it back, one step removed.
 */

export interface BrowsedChat {
  readonly peerId: number
  /**
   * A string, and never a number.
   *
   * Telegram's access hash is a random int64. A JavaScript number holds 53 bits
   * exactly, so about one hash in a thousand survives JSON.parse and the rest
   * arrive a few units wrong — and a wrong hash addresses nobody: Telegram
   * answers PEER_ID_INVALID and the panel says "adding the conversation
   * failed". It is carried as text from the server to here and back, and never
   * turned into a number on the way.
   */
  readonly accessHash: string
  readonly username: string
  readonly title: string
  /** The last message, for telling two conversations apart. */
  readonly preview: string
  /** Whether that last message was ours. */
  readonly out: boolean
  readonly lastMessageAt: string | undefined
  readonly unread: number
  /** False for a chat that cannot be added whatever anybody clicks. */
  readonly admissible: boolean
  /** Why not, in the scope filter's own vocabulary. */
  readonly refused: string
  /** Already in the CRM. */
  readonly known: boolean
}

export interface HistoryLine {
  readonly messageId: number
  readonly out: boolean
  readonly text: string
  readonly at: string
  readonly media: number
}

export interface AddedChat {
  readonly threadId: string
  /** How many messages this call stored. Zero on a second import. */
  readonly imported: number
  readonly read: number
  /** The oldest message imported — not the start of the conversation. */
  readonly from: string
  /**
   * That message's id. Sent back as `beforeId` to reach further back: without
   * it "more history" would read the same batch again and store nothing.
   */
  readonly oldestId: number
  /** Telegram still had older messages behind the batch. */
  readonly more: boolean
}

function chat(value: unknown): BrowsedChat {
  const raw = asObject(value, 'a conversation')

  return {
    peerId: num(raw, 'peer_id'),
    accessHash: str(raw, 'access_hash'),
    username: text(raw, 'username'),
    title: str(raw, 'title'),
    preview: text(raw, 'preview'),
    out: bool(raw, 'out'),
    lastMessageAt: optionalDate(raw, 'last_message_at'),
    unread: count(raw, 'unread'),
    admissible: bool(raw, 'admissible'),
    refused: text(raw, 'refused'),
    known: bool(raw, 'known'),
  }
}

function optionalDate(raw: Record<string, unknown>, name: string): string | undefined {
  const value = raw[name]
  return typeof value === 'string' && value !== '' ? value : undefined
}

function chats(body: unknown): BrowsedChat[] {
  const raw = asObject(body, 'the conversation list')
  return asArray(raw['dialogs'], 'dialogs').map(chat)
}

function line(value: unknown): HistoryLine {
  const raw = asObject(value, 'a message')
  return {
    messageId: num(raw, 'message_id'),
    out: bool(raw, 'out'),
    text: text(raw, 'text'),
    at: str(raw, 'at'),
    media: count(raw, 'media'),
  }
}

function lines(body: unknown): HistoryLine[] {
  const raw = asObject(body, 'the conversation')
  return asArray(raw['messages'], 'messages').map(line)
}

function added(body: unknown): AddedChat {
  const raw = asObject(body, 'the result')
  return {
    threadId: str(raw, 'thread_id'),
    imported: count(raw, 'imported'),
    read: count(raw, 'read'),
    from: text(raw, 'from'),
    oldestId: count(raw, 'oldest_message_id'),
    more: bool(raw, 'more'),
  }
}

export function fetchDialogs(signal?: AbortSignal): Promise<BrowsedChat[]> {
  return request('/api/channel/dialogs', chats, signal === undefined ? {} : { signal })
}

/**
 * Read a conversation without storing any of it.
 *
 * The access hash rides in the query string because it came from the listing
 * that produced this row: it addresses nothing without the account's own
 * session, and a wrong one simply fails.
 */
export function fetchChatHistory(
  chat: Pick<BrowsedChat, 'peerId' | 'accessHash'>,
  signal?: AbortSignal,
): Promise<HistoryLine[]> {
  const path = `/api/channel/dialogs/${chat.peerId}/history?access_hash=${chat.accessHash}`
  return request(path, lines, signal === undefined ? {} : { signal })
}

export function addChat(
  chat: Pick<BrowsedChat, 'peerId' | 'accessHash' | 'username'>,
  /** The oldest message already imported, to continue behind it. */
  beforeId = 0,
): Promise<AddedChat> {
  return request('/api/channel/chats', added, {
    method: 'POST',
    body: {
      peer_id: chat.peerId,
      access_hash: chat.accessHash,
      username: chat.username,
      before_id: beforeId,
    },
  })
}
